import React, { useCallback, useState } from "react";

import { useHistory } from "react-router-dom";

import usePostPortfolioPlansBatch from "@fartherfinance/frontend/api/Investment/hooks/usePostPortfolioPlansBatch";
import { PortfolioPlanId } from "@fartherfinance/frontend/api/Types";

import { InvestmentsPath } from "@src/config/routing/RouterPaths";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import { trackEvent } from "@src/multiCustodian/services/tracking";
import { safeAwait } from "@src/utils/safeAwait";

import CSVModalUpload from "./CSVModalUpload";

interface Props {
  showModal: boolean;
  closeModal: () => void;
  portfolioPlanId?: PortfolioPlanId;
}

const CSVUploadModalLauncher: React.FC<Props> = ({
  showModal,
  closeModal,
  portfolioPlanId,
}) => {
  const [clientCSV, setClientCSV] = useState<File | null>(null);
  const [validationErrors, setValidationErrors] = useState<null | string[]>(
    null
  );
  const [isUploadingBatch, setIsUploadingBatch] = useState(false);

  const history = useHistory();

  const auth = useAdvisorRequestAuth();

  const onCloseModal = useCallback(() => {
    setValidationErrors(null);
    setClientCSV(null);
    closeModal();
  }, [closeModal]);

  const notify = useStatusNotification();

  const { mutateAsync: postPortfolioPlansBatch } =
    usePostPortfolioPlansBatch(auth);

  const onPostPortfolioPlansBatch = useCallback(async () => {
    try {
      setIsUploadingBatch(true);

      if (clientCSV === null) {
        return;
      }

      // reset validation errors as we are starting a new run
      setValidationErrors(null);

      const csvUploadData = await safeAwait(
        postPortfolioPlansBatch({ data: clientCSV, dryRun: true })
      );

      // network issue
      if (csvUploadData.success === false) {
        notify("General error parsing file", "Error");
        return;
      }

      // Parsing issue
      if (csvUploadData.value.success === false) {
        notify(csvUploadData.value.error.message, "Error");
        setValidationErrors(csvUploadData.value.error.details);
        return;
      }

      const createdPortfolioPlansData = await safeAwait(
        postPortfolioPlansBatch({ data: clientCSV, dryRun: false })
      );

      // network issue
      if (createdPortfolioPlansData.success === false) {
        notify("General error saving file", "Error");
        return;
      }

      // Parsing issue
      if (createdPortfolioPlansData.value.success === false) {
        notify(createdPortfolioPlansData.value.error.message, "Error");
        setValidationErrors(createdPortfolioPlansData.value.error.details);
        return;
      }

      notify("Portfolio Created", "Success");

      trackEvent({
        name: "Advisor Upload CSV To Create or Update Portfolios",
      });

      if (
        !portfolioPlanId ||
        createdPortfolioPlansData.value.data.every(
          (pp) => pp.portfolioPlanId !== portfolioPlanId
        )
      ) {
        history.push(`/Advisor/${InvestmentsPath}/ModelMarketplaceV2`);
      }

      onCloseModal();
    } finally {
      setIsUploadingBatch(false);
    }
  }, [
    clientCSV,
    onCloseModal,
    notify,
    postPortfolioPlansBatch,
    portfolioPlanId,
    history,
  ]);

  return (
    <>
      {showModal && (
        <CSVModalUpload
          isOpen={showModal}
          onClose={onCloseModal}
          clientCSV={clientCSV}
          setClientCSV={setClientCSV}
          isMutating={isUploadingBatch}
          onSubmit={onPostPortfolioPlansBatch}
          validationErrors={validationErrors}
        />
      )}
    </>
  );
};

export default CSVUploadModalLauncher;
